import { BRAND_HEX } from '../../styles/brand-colors';
import type { ButtonVariantProps } from './Button.variants';

/**
 * Native shadow styles per variant. Applied via Pressable's `nativeProps.style`
 * on React Native because NativeWind v4's `shadow-*` utility compilation to
 * RN's shadow props is unreliable — and the Figma "Shadow-Button-*" effects
 * need to render visibly on both iOS and Android to match the web bevel.
 *
 * On web, the Tailwind `shadow-btn-{variant}` class on the same Pressable
 * supplies an equivalent CSS `box-shadow` and takes effect via the browser;
 * these native style props are ignored.
 *
 * Style fields targeted (RN >= 0.65):
 *   shadowColor   – iOS / Android 9+
 *   shadowOffset  – iOS only
 *   shadowOpacity – iOS only
 *   shadowRadius  – iOS only
 *   elevation     – Android only (also enables the system shadow)
 *
 * Values mirror the Figma "Shadow-Button-*" effects on the section
 * `Button (Label +Text)` (node 294:1855). Hex colors are pulled from the
 * shared `BRAND_HEX` constants so the design-system palette is the single
 * source of truth for both Tailwind classes and RN shadow props.
 */
type Variant = NonNullable<ButtonVariantProps['variant']>;

interface NativeShadow {
  shadowColor: string;
  shadowOffset: { width: number; height: number };
  shadowOpacity: number;
  shadowRadius: number;
  elevation: number;
}

export const NATIVE_SHADOW_FOR: Record<Variant, NativeShadow | undefined> = {
  // Shadow-Button-Primary: #8E51FF80 (primary-500 @ 50%), offset (0,2), blur 8
  primary: {
    shadowColor: BRAND_HEX.primary['500'],
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.5,
    shadowRadius: 4,
    elevation: 4,
  },
  // Shadow-button-Green: #00C9504D (green-500 @ 30%), offset (0,2), blur 8
  success: {
    shadowColor: BRAND_HEX.green['500'],
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
  // Shadow-Button-Red: #FB2C364D (red-500 @ 30%), offset (0,2), blur 8
  alert: {
    shadowColor: BRAND_HEX.red['500'],
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
  // Shadow-Secondary: #0000000F (black @ 6%), offset (0,2), blur 4
  secondary: {
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 1,
  },
  tertiary: undefined,
  // Ghost variant — transparent bg/border at rest, no drop shadow.
  ghost: undefined,
};
